import React, { useState } from 'react';

import { apiUrl } from '../utils/apiBase';
import type { MatchItem, MatchRules, SetEntryOptions } from './scoring-types';

export type SetValue = { p1: number | ''; p2: number | '' };
export type Winner = 1 | 2 | null;

function isCanonicalSet(setEntry: SetEntryOptions, set: SetValue): boolean {
  if (set.p1 === '' || set.p2 === '') {
    return false;
  }

  return setEntry.winnerByLoser[String(Math.min(set.p1, set.p2))] === Math.max(set.p1, set.p2);
}

export function setViolation(setEntry: SetEntryOptions, set: SetValue): string | null {
  if (set.p1 === '' || set.p2 === '' || isCanonicalSet(setEntry, set)) {
    return null;
  }

  if (set.p1 === set.p2) {
    return 'Remis w secie nie jest możliwy.';
  }

  const loser = Math.min(set.p1, set.p2);
  const expected = setEntry.winnerByLoser[String(loser)];

  if (expected === undefined) {
    return `Wynik ${set.p1}:${set.p2} nie jest możliwy w tym formacie.`;
  }

  return `Przy ${loser} zwycięzca ma ${expected}, nie ${Math.max(set.p1, set.p2)}.`;
}

function withCompletingScore(
  match: MatchItem,
  set: SetValue,
  side: 'p1' | 'p2',
  value: number | '',
): SetValue {
  const next: SetValue = side === 'p1' ? { ...set, p1: value } : { ...set, p2: value };

  if (value === '' || value >= match.rules.pointsPerSet) {
    return next;
  }

  const winner = match.setEntry.winnerByLoser[String(value)];

  if (winner === undefined || isCanonicalSet(match.setEntry, next)) {
    return next;
  }

  return side === 'p1' ? { ...next, p2: winner } : { ...next, p1: winner };
}

export function plannedSetCount(rules: MatchRules): number {
  return rules.setsPlayed ?? rules.bestOf;
}

export function initialSets(match: MatchItem): SetValue[] {
  return Array.from({ length: plannedSetCount(match.rules) }, (_, index) => {
    const played = match.sets[index];

    if (!played) {
      return { p1: '', p2: '' };
    }

    return { p1: played.p1, p2: played.p2 };
  });
}

function setsToWin(bestOf: number): number {
  return Math.ceil(bestOf / 2);
}

export function computeWinner(sets: SetValue[], rules: MatchRules): Winner {
  const decided = sets.filter((set) => set.p1 !== '' && set.p2 !== '' && set.p1 !== set.p2);
  const side1 = decided.filter((set) => set.p1 > set.p2).length;
  const side2 = decided.length - side1;

  if (rules.setsPlayed != null) {
    if (decided.length < rules.setsPlayed || side1 === side2) {
      return null;
    }

    return side1 > side2 ? 1 : 2;
  }

  const needed = setsToWin(rules.bestOf);

  if (side1 >= needed && side1 > side2) {
    return 1;
  }

  if (side2 >= needed && side2 > side1) {
    return 2;
  }

  return null;
}

export function isDraw(sets: SetValue[], rules: MatchRules): boolean {
  if (rules.setsPlayed == null) {
    return false;
  }

  const decided = sets.filter((set) => set.p1 !== '' && set.p2 !== '' && set.p1 !== set.p2);
  const side1 = decided.filter((set) => set.p1 > set.p2).length;

  return decided.length === rules.setsPlayed && side1 * 2 === decided.length;
}

export function visibleSetCount(sets: SetValue[], rules: MatchRules): number {
  if (rules.setsPlayed != null) {
    return rules.setsPlayed;
  }

  const needed = setsToWin(rules.bestOf);
  let side1 = 0;
  let side2 = 0;

  for (let index = 0; index < rules.bestOf; index += 1) {
    const set = sets[index];

    if (!set || set.p1 === '' || set.p2 === '') {
      return Math.max(needed, index + 1);
    }

    if (set.p1 > set.p2) {
      side1 += 1;
    } else if (set.p2 > set.p1) {
      side2 += 1;
    }

    if (side1 >= needed || side2 >= needed) {
      return Math.max(needed, index + 1);
    }
  }

  return rules.bestOf;
}

export function currentWinner(match: MatchItem): Winner {
  if (match.winnerSide === 'SIDE1') {
    return 1;
  }

  if (match.winnerSide === 'SIDE2') {
    return 2;
  }

  return null;
}

export interface MatchScoreDraft {
  sets: SetValue[];
  visibleSets: SetValue[];
  winner: Winner;
  blocked: boolean;
  submitting: boolean;
  error: string | null;
  updateSet: (index: number, side: 'p1' | 'p2', raw: string) => void;
  reset: () => void;
  submit: (event: React.FormEvent) => Promise<void>;
}

export function useMatchScoreDraft(
  match: MatchItem,
  token: string,
  onSaved: () => void,
): MatchScoreDraft {
  const [sets, setSets] = useState<SetValue[]>(() => initialSets(match));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const winner = computeWinner(sets, match.rules);
  const visibleSets = sets.slice(0, visibleSetCount(sets, match.rules));
  const blocked = visibleSets.some((set) => setViolation(match.setEntry, set) !== null);

  function updateSet(index: number, side: 'p1' | 'p2', raw: string): void {
    const parsed = raw === '' ? '' : Number(raw);
    const value = parsed === '' || Number.isNaN(parsed) ? '' : parsed;

    setSets((previous) =>
      previous.map((set, i) => (i === index ? withCompletingScore(match, set, side, value) : set)),
    );
  }

  function reset(): void {
    setSets(initialSets(match));
    setError(null);
  }

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();

    if (winner === null) {
      setError(
        isDraw(sets, match.rules)
          ? 'Remis — zapisywanie remisów nie jest jeszcze obsługiwane.'
          : 'Uzupełnij sety — nie da się ustalić zwycięzcy.',
      );

      return;
    }

    const filled = sets
      .filter((set) => set.p1 !== '' && set.p2 !== '')
      .map((set) => ({ p1: Number(set.p1), p2: Number(set.p2) }));

    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch(
        apiUrl(`/api/public/scoring/${token}/matches/${match.id}/result`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sets: filled, winnerSide: winner }),
        },
      );

      if (response.status === 429) {
        setError('Zbyt wiele żądań — poczekaj chwilę i spróbuj ponownie.');

        return;
      }

      if (response.status === 403) {
        setError('Kolejka jest już zamknięta — wyniki niedostępne do edycji.');

        return;
      }

      if (!response.ok) {
        const body = await response.json() as { error?: string };
        setError(body.error ?? `Błąd (${response.status})`);

        return;
      }

      onSaved();
    } catch {
      setError('Błąd sieci — sprawdź połączenie.');
    } finally {
      setSubmitting(false);
    }
  }

  return { sets, visibleSets, winner, blocked, submitting, error, updateSet, reset, submit };
}
